/**
 * 4-piece set active-stack bonuses — TU22.1 Rise Up verified (April 2026).
 * Sources: fandom wiki (updated 2024-2026), kboosting.com guides,
 * Divisionalysis mannequin tests, s-i-n.co.uk.
 */

export interface SetStacks {
  /** ID in our new naming (slugified English set name) */
  setId: string;
  name: { en: string; ru: string };
  trigger: 'hit' | 'hs' | 'kill' | 'tempo' | 'proc';
  triggerNote: { en: string; ru: string };
  /** Maximum stacks without chest/backpack perks */
  maxBase: number;
  /** Optional increased cap when chest talent of the set is worn */
  maxChest?: number;
  /** Stat each stack adds and its % per stack */
  perStack: Array<{ stat: 'wd' | 'chd' | 'chc' | 'hsd' | 'rof' | 'handling'; value: number }>;
  /** When backpack talent of the set is equipped, per-stack value may differ */
  perStackBp?: Array<{ stat: 'wd' | 'chd' | 'chc' | 'hsd' | 'rof' | 'handling'; value: number }>;
  /** True if the WD bonus goes into the amplified bucket (own multiplier) vs additive. */
  wdAmplified?: boolean;
  /** Set bonus only applies while the target is pulsed (Heartbreaker). */
  requiresPulsed?: boolean;
  /** Set bonus only applies while the target is marked (Negotiator's Dilemma). */
  requiresMarked?: boolean;
  note?: { en: string; ru: string };
}

export const SET_4PC_STACKS: SetStacks[] = [
  {
    setId: 'striker_s_battlegear',
    name: { en: "Striker's Battlegear", ru: 'Снаряжение боевика' },
    trigger: 'hit',
    triggerNote: {
      en: 'Per hit: +0.65% WD. Decay: 1/s (0-50), 2/s (51-100), 3/s (101-200 with chest).',
      ru: 'За попадание: +0.65% WD. Спад: 1/с (0-50), 2/с (51-100), 3/с (101-200 с chest).',
    },
    maxBase: 100,
    maxChest: 200,
    perStack: [{ stat: 'wd', value: 0.65 }],
    perStackBp: [{ stat: 'wd', value: 0.9 }],
    // v1 data: "Amplifier, stacks additively" — (1 + 0.0065*n) applied as its OWN multiplier
    // outside the main WD bucket. Not additive with weapon damage stat.
    wdAmplified: true,
    note: {
      en: '100 stacks × 0.65%/0.9% applied as AMPLIFIED multiplier (own bucket, not additive WD).',
      ru: '100 стеков × 0.65%/0.9% — AMPLIFIER (отдельный множитель, не складывается с WD).',
    },
  },
  {
    setId: 'heartbreaker',
    name: { en: 'Heartbreaker', ru: 'Разбиватель сердец' },
    trigger: 'hs',
    triggerNote: {
      en: 'HS pulses target; hits on pulsed grant +1% WD + +1% bonus armor/stack.',
      ru: 'Хедшот пульсирует цель; попадания по пульсированным → +1% WD + +1% брони/стак.',
    },
    maxBase: 50,
    maxChest: 100,  // Max BPM chest talent
    perStack: [{ stat: 'wd', value: 1.0 }],  // WD bonus only vs pulsed
    requiresPulsed: true,  // Bonus gated — applied only when state.targetPulsed = true.
    note: {
      en: 'WD bonus applies only to pulsed enemies. 50 stacks base → +50% vs pulsed, 100 with chest. Activate "target pulsed" toggle to apply.',
      ru: 'Бонус WD только по пульсированным. 50 стеков → +50%, 100 с chest. Включи переключатель «цель пульсирована» чтобы применить.',
    },
  },
  {
    setId: 'umbra_initiative',
    name: { en: 'Umbra Initiative', ru: 'Инициатива Умбра' },
    trigger: 'tempo',
    triggerNote: {
      en: 'In cover: +10/s (gain From the Shadows). Out: -2/s standing, -1/s sprint.',
      ru: 'В укрытии: +10/с (From the Shadows). Вне: -2/с стоя, -1/с спринт.',
    },
    maxBase: 50,
    maxChest: 100,  // From the Shadows chest talent: 50→100
    perStack: [
      { stat: 'chd', value: 1.2 },    // TU22 buff: was 1.0% → 1.2% CHD per stack
      { stat: 'rof', value: 0.4 },    // TU22 buff: was 0.3% → 0.4% RoF per stack
    ],
    note: {
      en: 'TU22 buffed: +1.2% CHD, +0.4% RPM/stack. Max 50 = +60% CHD +20% RPM; 100 = +120%/+40%.',
      ru: 'TU22 баф: +1.2% CHD, +0.4% RPM/стак. Макс 50 = +60% CHD +20% RPM; 100 = +120%/+40%.',
    },
  },
  {
    setId: 'hunter_s_fury',
    name: { en: "Hunter's Fury", ru: 'Ярость охотника' },
    trigger: 'kill',
    triggerNote: {
      en: 'Kill of debuffed enemy (<15m) grants +5% amplified WD stack, 10s (30s with chest).',
      ru: 'Убийство дебафнутого (<15м) даёт +5% амплиф. WD стак, 10с (30с с chest).',
    },
    maxBase: 5,
    perStack: [{ stat: 'wd', value: 5 }],
    wdAmplified: true,  // Hunter's Fury WD is AMPLIFIED, not additive
    note: {
      en: 'AMPLIFIED bucket. Max +25% amplified WD (5 stacks × 5%). Chest: 30s duration.',
      ru: 'AMPLIFIED множитель. Макс +25% амплиф. WD (5 × 5%). Chest: 30с длительность.',
    },
  },
  {
    setId: 'negotiator_s_dilemma',
    name: { en: "Negotiator's Dilemma", ru: 'Дилемма переговорщика' },
    trigger: 'proc',
    triggerNote: {
      en: 'Hit → marks target. Up to 5 marks. On kill: transfer to nearest.',
      ru: 'Попадание помечает цель. До 5 меток. При убийстве: перенос.',
    },
    maxBase: 5,
    perStack: [{ stat: 'wd', value: 10 }],  // +10% damage to marked targets per mark (amp-like)
    wdAmplified: true,
    requiresMarked: true,  // Bonus gated — applied only when state.targetMarked = true.
    note: {
      en: 'Amplified. 5 marks = +50% damage to marked (only to marked). Activate "target marked" toggle to apply.',
      ru: 'Амплиф. 5 меток = +50% урон по меченым (только меченым). Включи переключатель «цель помечена» чтобы применить.',
    },
  },
  {
    setId: 'tipping_scales',
    name: { en: 'Tipping Scales', ru: 'Чаша весов' },
    trigger: 'hit',
    triggerNote: {
      en: 'Per shot: +4% CHD, +0.5% handling. Decay: -6/s not firing. No decay if enemy suppressed.',
      ru: 'За выстрел: +4% CHD, +0.5% эргономика. Спад: -6/с без стрельбы. Нет спада если цель подавлена.',
    },
    maxBase: 50,
    maxChest: 75,  // Sustainability chest talent: 50→75
    perStack: [
      { stat: 'chd', value: 4 },
      { stat: 'handling', value: 0.5 },
    ],
    perStackBp: [
      { stat: 'chd', value: 7 },  // Snowball backpack: 4→7% CHD
      { stat: 'handling', value: 0.5 },
    ],
    note: {
      en: 'At 75 stacks × 7% = +525% CHD — bracket-high CHD build.',
      ru: 'При 75 × 7% = +525% CHD — топ по CHD.',
    },
  },
  {
    setId: 'future_initiative',
    name: { en: 'Future Initiative', ru: 'Инициатива будущего' },
    trigger: 'proc',
    triggerNote: {
      en: 'At full armor: baseline +15% WD/SD for you and allies (25% with backpack).',
      ru: 'При полной броне: +15% WD/SD для себя и союзников (25% с backpack).',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 15 }],
    perStackBp: [{ stat: 'wd', value: 25 }],  // Tactical Superiority backpack
    note: {
      en: 'Goes inactive when armor breaks; refresh at full armor.',
      ru: 'Отключается при пробитии брони; активируется при полной броне.',
    },
  },
  {
    setId: 'hard_wired',
    name: { en: 'Hard Wired', ru: 'Жёстко зашитый' },
    trigger: 'proc',
    triggerNote: {
      en: 'Use/cancel skill → -30s other skill CD + +10% SD/Repair 20s (25% with chest). ICD 20s (10s with backpack).',
      ru: 'Исп./отмена навыка → -30с CD другого + +10% SD/Repair 20с (25% с chest). ICD 20с (10с с backpack).',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],  // Skill damage only, not weapon
    note: {
      en: 'Skill builds only — no weapon damage component.',
      ru: 'Только для скилл-билдов — нет бонуса к оружейному урону.',
    },
  },
  {
    setId: 'tip_of_the_spear',
    name: { en: 'Tip of the Spear', ru: 'Остриё копья' },
    trigger: 'proc',
    triggerNote: {
      en: 'Signature weapon kill: +15% Sig WD 10s (30% with chest). Auto-regen sig ammo every 60s.',
      ru: 'Убийство signature оружием: +15% Sig WD 10с (30% с chest). Авто-регенерация боеприпасов сигнатуры каждые 60с.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 15 }],  // Signature weapon only
    note: {
      en: '3pc: +10% WD. Bonus applies only to signature weapon damage, not regular weapons.',
      ru: '3шт: +10% WD. Бонус только для signature-оружия, не обычного.',
    },
  },
  {
    // ⚠ NEEDS IN-GAME VERIFICATION (group-scaling exact %).
    // Mechanic: Red group-coop set. chest "All for One" / bp "One for All" — share
    // bonuses with allies. 4pc: WD bonus scales with group size, peaks при full
    // group of 4 (solo gets a fraction). Community TU22.1: peak ~+25% WD additive
    // at group-of-4.
    // Bucket: ADDITIVE WD. Calc shows GROUP PEAK (build state has no groupSize gate
    // for damage formula — see comment in build-state at "Group size does NOT
    // affect player damage").
    setId: 'concentrated_company',
    name: { en: 'Concentrated Company', ru: 'Сосредоточенная команда' },
    trigger: 'proc',
    triggerNote: {
      en: 'Group-coop bonus. Peak at group of 4. Solo applies a fraction.',
      ru: 'Бонус group-coop. Пик в группе из 4. Соло — часть.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 25 }],
    note: {
      en: '⚠ Peak +25% WD additive at full group of 4 (community estimate). Calc applies always — overestimates DPS solo. Pending in-game verify.',
      ru: '⚠ Пик +25% WD additive в группе из 4 (community estimate). Calc применяет всегда — overestimate для соло. Требуется проверка in-game.',
    },
  },
  {
    // Ongoing Directive — bleed-applier set. 4pc: kills auto-reload mag and apply
    // bleed to nearby enemies; chest "Hollow Point Ammo" / bp "Tactical Superiority"
    // (different from Future Initiative bp of same name in older patches; here the
    // set's bp talent is "tactical_superiority" or related — community varies).
    // Bleed itself is a DoT (separate pipeline), but THIS set acts as a guaranteed
    // bleed-applier — pairs with Sadist / Perfect Sadist for amp WD vs bleeding.
    // No direct WD bonus from the 4pc itself — perStack=0. Auto-bleed applies to
    // the target via state.targetBleeding when this set is 4pc-equipped (see formula).
    setId: 'ongoing_directive',
    name: { en: 'Ongoing Directive', ru: 'Постоянная директива' },
    trigger: 'kill',
    triggerNote: {
      en: 'Kills apply bleed to nearby enemies and auto-reload magazine. Bleed gates Sadist amp.',
      ru: 'Убийство поджигает кровотечение у соседних врагов и авто-перезаряжает магазин. Кровотечение активирует Sadist amp.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '4pc itself has no direct WD bonus — combo with Sadist (chest, +20% amp) or Perfect Sadist (+25% amp) for bleed-conditional amplification. Set auto-applies bleed: targetBleeding becomes effective true when this set is 4pc-equipped (see appliesBleed pathway).',
      ru: '4pc не даёт прямого бонуса WD — combo с Sadist (chest, +20% amp) или Perfect Sadist (+25% amp) для bleed-conditional амп. Сет сам поджигает: targetBleeding становится effective=true при 4pc (см. appliesBleed pipeline).',
    },
  },
  {
    // ⚠ Yellow skill-tier set — weapon DPS impact minimal/uncertain.
    // Mechanic: chest "Target Rich Environment" — crit-dependent bonuses (mostly
    // skill-side?). bp "Critical Measures" — crit / skill use synergy. Community
    // data sparse for weapon DPS contribution; main use is skill-builds.
    // Modeled as +0% wd placeholder until in-game verification reveals concrete
    // weapon-DPS effect (if any).
    setId: 'measured_assembly',
    name: { en: 'Measured Assembly', ru: 'Точная сборка' },
    trigger: 'proc',
    triggerNote: {
      en: 'Yellow skill set — crit/skill synergy. Weapon DPS impact minimal/uncertain.',
      ru: 'Yellow skill сет — синергия с критами/навыками. Влияние на оружие минимально/неясно.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ Skill-tier set — weapon DPS contribution unverified, modelled as 0. Update if user finds concrete weapon-DPS bonus.',
      ru: '⚠ Skill-tier сет — вклад в weapon DPS не проверен, моделируется как 0. Обновить если user найдёт конкретный бонус.',
    },
  },
  {
    // ⚠ NEEDS IN-GAME VERIFICATION (Ortiz peak vs burning %).
    // Mechanic: Yellow set (skill-tier). Applies burn on hit; chain ignites nearby
    // enemies. Bonus damage to burning enemies. chest "Chain Combustion" — burn
    // chains. bp "Heatstroke" — bonus damage vs burning amplified.
    // Community TU22.1: peak vs burning ~25-30% additive WD (sparse data).
    // Bucket: ADDITIVE WD (gated to burning target — calc shows peak so always-on).
    setId: 'ortiz_exuro',
    name: { en: 'Ortiz: Exuro', ru: 'Ортис: Экзуро' },
    trigger: 'proc',
    triggerNote: {
      en: 'Hit applies burn; bonus damage to burning enemies. Chest chains burn, bp amps damage.',
      ru: 'Попадания поджигают; бонус урон по горящим. Chest — цепная реакция, bp — амп урона.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 25 }],
    note: {
      en: '⚠ Peak +25% WD additive vs burning (community estimate). Burn-status setter — calc assumes target burning.',
      ru: '⚠ Пик +25% WD additive по горящей цели (community estimate). Поджигает сам — calc применяет peak.',
    },
  },
  {
    // ⚠ NEEDS IN-GAME VERIFICATION (Breaking Point peak %, mechanic shape).
    // Mechanic: Rifle/MMR red set. Shots pierce/strip armor; bonus damage scales
    // with target armor state (or stacks per armor break). chest "Point of No Return"
    // — per-stack scaling. bp "Point of Honor" — bonus on armor → health swap.
    // Community TU22.1: peak ~30-40% amp WD (sparse); some sources describe stack
    // ladder per armor strike (max 5-10 stacks × 3-5% amp).
    // Modeled here as 1 × 30% AMPLIFIED WD peak — single-tier placeholder until
    // user verification refines the stack ladder.
    setId: 'breaking_point',
    name: { en: 'Breaking Point', ru: 'Точка перелома' },
    trigger: 'proc',
    triggerNote: {
      en: 'Rifle/MMR set. Armor strikes build stacks: +4% WD/stack base, +6% with backpack. Max 6 stacks.',
      ru: 'Rifle/MMR сет. Удары по броне строят стаки: +4% WD/стак, +6% с backpack. Макс 6 стэков.',
    },
    maxBase: 6,
    perStack: [{ stat: 'wd', value: 4 }],
    perStackBp: [{ stat: 'wd', value: 6 }],
    wdAmplified: true,
    note: {
      en: 'TU22.1 PvE: 6 × 4% = +24% amp WD base; 6 × 6% = +36% amp WD with backpack (Point of Honor). Per Wiki / 2026 build guides.',
      ru: 'TU22.1 PvE: 6 × 4% = +24% amp WD; 6 × 6% = +36% amp WD с backpack (Point of Honor). Wiki / 2026 source.',
    },
  },
  {
    // ⚠ NEEDS IN-GAME VERIFICATION (peak HSD %).
    // Mechanic: HS shots build stacks of +1% HSD each. Decay on miss / no-fire.
    // Community canon TU22.1: max 60 stacks base → +60% HSD; chest "Endless Hunger"
    //   raises cap (community varies 80-100); bp "Overwhelming Force" allegedly
    //   converts a portion of HSD into bonus WD at peak (TBD, not modeled here).
    // Bucket: HSD pool (additive with intrinsic HSD + SHD watch HSD).
    // Calc shows PEAK = 60 stacks × 1% (or 100 × 1% with chest).
    setId: 'hotshot',
    name: { en: 'Hotshot', ru: 'Сорвиголова' },
    trigger: 'hs',
    triggerNote: {
      en: 'HS adds +1% HSD per stack. Max 60 (100 with chest "Endless Hunger").',
      ru: 'Хедшот: +1% HSD за стак. Макс 60 (100 с chest "Endless Hunger").',
    },
    maxBase: 60,
    maxChest: 100,
    perStack: [{ stat: 'hsd', value: 1 }],
    note: {
      en: '⚠ Peak +60% HSD additive (100% with chest). Backpack "Overwhelming Force" wd-conversion not modeled — pending in-game verification.',
      ru: '⚠ Пик +60% HSD additive (100% с chest). bp "Overwhelming Force" — конвертация HSD→WD не моделируется, требуется проверка in-game.',
    },
  },
  {
    // ⚠ APPROXIMATION (enemy debuff modelled as self amp WD).
    // Mechanic: flag rotation Red / White / Blue debuffs the targeted enemy.
    //   Red — enemy takes more damage from weapons (~25-30%, community).
    //   White — armor regen self/allies.
    //   Blue — skill damage / repair amp.
    // Math: Red flag = enemy-side vulnerability multiplier; equivalent to a self
    // amplified WD multiplier of the same magnitude (commutative). Calc auto-applies
    // Red-flag peak as +25% amp. White / Blue not modeled (defensive / skill).
    // Implementation note: amplified bucket (own multiplier), not additive — debuff
    // stacks like Hunter's Fury / Equalizer / FI.
    setId: 'true_patriot',
    name: { en: 'True Patriot', ru: 'Истинный патриот' },
    trigger: 'proc',
    triggerNote: {
      en: 'Red flag debuff on target = enemy takes +25% damage. Modelled as self amp WD.',
      ru: 'Красный флаг (дебафф цели) = +25% входящего урона. Моделируется как self amp WD.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 25 }],
    wdAmplified: true,
    note: {
      en: '⚠ Approximation: actual mechanic is enemy-side debuff, calc treats Red flag as self +25% amp WD. Peak %: needs in-game verification.',
      ru: '⚠ Приближение: реальная механика — дебафф врага. Calc применяет Red flag как self +25% amp WD. Пик: требуется проверка in-game.',
    },
  },
  {
    // ⚠ NEEDS IN-GAME VERIFICATION (single-bullet WD scaling).
    // Mechanic: rhythmic-reload sniper/MMR set. Each shot of high-precision (single-
    // bullet weapon) builds stacks; reload at the right tempo grants peak buff.
    //   chest "Fortissimo" — at peak, weapon fires "loud" notes = bigger WD.
    //   bp "Accelerando" — RoF acceleration on missed beats.
    // Community canon (sparse, TU22.1): peak +50% WD additive single-bullet only.
    // Bucket: ADDITIVE WD (per community testing), single-bullet weapons (MMR / Rifle / sniper-pistol).
    // Calc shows PEAK = 1 stack × 50% additive WD.
    setId: 'virtuoso',
    name: { en: 'Virtuoso', ru: 'Виртуоз' },
    trigger: 'proc',
    triggerNote: {
      en: 'Single-bullet rhythm: every shot at right tempo builds stacks. Peak = max-rhythm reload.',
      ru: 'Single-bullet ритм: попадания в темп строят стэки. Пик при reload в темп.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 50 }],
    note: {
      en: '⚠ Peak +50% WD additive (community estimate, needs in-game verification). Bonus only on single-bullet weapons (MMR / Rifle / sniper-pistols).',
      ru: '⚠ Пик +50% WD additive (community estimate, требуется проверка). Бонус только для single-bullet оружий (MMR / Rifle / sniper-pistol).',
    },
  },
  {
    // ⚠ NEEDS IN-GAME VERIFICATION (peak Royal Flush exact %).
    // Mechanic: HS build cards into a 5-card poker hand. Each rung up the
    // hand-rank ladder (Pair → Two Pair → ... → Royal Flush) gives a larger WD bonus.
    // Community data points 2024-2026 (kboosting, fandom, s-i-n.co.uk):
    //   Pair ≈ 5%, Two Pair 10%, Three of a Kind 20%, Straight 30%, Flush 40%,
    //   Full House 50%, Four of a Kind 75%, Straight Flush 90%, Royal Flush 100%.
    // Calculator shows PEAK (Royal Flush) as is convention for other 4pc sets.
    // Bucket: ADDITIVE per community testing (stacks with other WD, not its own multiplier).
    // Chest "Aces High" — guarantees deck draws on kills (allows reaching higher hands faster).
    // Backpack "Dead Man's Hand" — kill-trigger seeds a pair → tempo into Royal Flush.
    // We model maxBase=1 stack ×100% WD as Royal Flush peak. User to verify exact peak %.
    setId: 'aces_eights',
    name: { en: 'Aces & Eights', ru: 'Тузы и восьмёрки' },
    trigger: 'hs',
    triggerNote: {
      en: 'HS draws cards into a 5-card poker hand. Hand rank → WD bonus. Royal Flush = peak.',
      ru: 'Хедшоты собирают 5-карточную комбинацию. Ранг руки → бонус WD. Флэш-рояль = пик.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 100 }],  // Royal Flush peak — needs verification
    note: {
      en: '⚠ Royal Flush peak = +100% WD additive (community estimate, needs in-game verification).',
      ru: '⚠ Пик «Флэш-рояль» = +100% WD additive (community estimate, требуется проверка in-game).',
    },
  },
  {
    // Y8S1 random-core mechanic. NOT a stack mechanic.
    // For each Core attribute on your gear, you receive 40% (75% with Inner Core
    // chest talent) of the OTHER two Core Attributes' standard bonuses.
    // raw gearsets.json (gear_set_*, Core Strength): WeaponHandling 10%, Damage 5%,
    //   ArmorModBonus 5% — these are baseline buffs, NOT the cross-core mechanic.
    // The cross-core math is handled by a SPECIAL HANDLER in build-state.svelte.ts
    //   (search: `setCounts['core_strength']`). It reads gear coreStats and applies
    //   the 40%/75% conversion ladder. This entry exists for UI panel + docs only.
    setId: 'core_strength',
    name: { en: 'Core Strength', ru: 'Сила мышечного корсета' },
    trigger: 'hit',
    triggerNote: {
      en: 'Per Core: 40% of other Cores\' standard bonuses (75% with Inner Core chest). Auto-applied.',
      ru: 'За каждый Core: 40% от стандартных бонусов других Cores (75% с Inner Core жилетом). Авто.',
    },
    maxBase: 1,
    maxChest: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: 'Y8S1 random-core mechanic — special handler in computeBuild reads gear cores and applies 40%/75% of other cores standard bonuses. NOT a stack mechanic. Slider has no effect; bonus is computed automatically from coreStat distribution.',
      ru: 'Y8S1 random-core механика — спец-обработчик в computeBuild читает Core у одежды и применяет 40%/75% бонусов других Cores. НЕ стек-механика. Слайдер не влияет; бонус считается автоматически по распределению Cores.',
    },
  },
  {
    // raw gearsets.json (gear_set_v Refactor): AllStatusEffectsModBonus 15% baseline
    // + 25% bonus from set ref (likely Status Damage / Status Effects ramp on hit).
    // Y8S1 status build — community canon (sparse, June 2026): hits build status-effect
    // stacks (max ~5-10), peak adds substantial Status Effects bonus. No direct WD
    // contribution from 4pc itself — buffs status DoT pipeline, not raw weapon damage.
    // Modeled as +0% wd (status pipeline is separate from weapon DPS bucket).
    setId: 'refactor',
    name: { en: 'Refactor', ru: 'Рефакторинг' },
    trigger: 'hit',
    triggerNote: {
      en: 'Y8S1 status build. Hits ramp Status Effects (raw shows 15% + 25% baseline). No direct WD impact.',
      ru: 'Y8S1 status билд. Попадания накапливают Status Effects (raw: 15% + 25% базы). Прямого WD нет.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ needs in-game verification — raw incomplete. raw shows +15% AllStatusEffectsModBonus + 25% set ref. Stack ladder and peak Status Effects % not verified. Status build — no direct WD contribution; calc treats as utility set.',
      ru: '⚠ требует проверки in-game — raw неполный. raw: +15% AllStatusEffectsModBonus + 25% set ref. Лестница стеков и пик Status Effects не проверены. Status билд — прямого WD нет; calc считает utility.',
    },
  },
  {
    // raw gearsets.json (gear_set_k Eclipse Protocol): AllStatusEffectsModBonus 15%,
    // SkillHasteModBonus 15%, AllStatusResistancePercentageFlatBonus 30%.
    // Status applier (community: applies Bleed/Burn/Shock on hit, ramps Status Effects).
    // Utility set — no direct weapon DPS contribution.
    setId: 'eclipse_protocol',
    name: { en: 'Eclipse Protocol', ru: 'Протокол «Затмение»' },
    trigger: 'proc',
    triggerNote: {
      en: 'Status applier (Bleed/Burn). Utility set — no direct WD. raw: +15% StatusEffects, +15% SkillHaste, +30% StatusResist.',
      ru: 'Поджигатель статусов (Bleed/Burn). Utility — прямого WD нет. raw: +15% StatusEffects, +15% SkillHaste, +30% StatusResist.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ needs in-game verification — raw incomplete. Confirmed baseline: +15% Status Effects + +15% Skill Haste + +30% Status Resistance. Status applier mechanic (Bleed/Burn proc) — separate pipeline, no direct WD. Calc treats as utility.',
      ru: '⚠ требует проверки in-game — raw неполный. Базы: +15% Status Effects + +15% Skill Haste + +30% Status Resistance. Поджигатель статусов — отдельный pipeline, прямого WD нет. Calc считает utility.',
    },
  },
  {
    // raw gearsets.json (gear_set_h Foundry Bulwark): ArmorModBonus 10%,
    // ArmorRegenFlatBonusModBonus 1% (≈ +1% armor regen per second), 50% bonus ref
    // (likely Armor Regen amplification or armor-on-kill).
    // Defensive armor-regen utility. No direct weapon DPS contribution.
    setId: 'foundry_bulwark',
    name: { en: 'Foundry Bulwark', ru: 'Сталелитейный бастион' },
    trigger: 'hit',
    triggerNote: {
      en: 'Armor regen utility. raw: +10% Armor, +1% Armor Regen, +50% bonus. Defensive set — no direct WD.',
      ru: 'Армор-реген utility. raw: +10% Armor, +1% Armor Regen, +50% бонус. Защитный сет — прямого WD нет.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ needs in-game verification — raw incomplete. Confirmed baseline: +10% Armor + +1% Armor Regen + 50% ref bonus. Defensive utility — no weapon DPS contribution. Per-stack armor regen ramp (community describes hit-driven regen stacks) not modeled.',
      ru: '⚠ требует проверки in-game — raw неполный. Базы: +10% Armor + +1% Armor Regen + 50% ref. Защитная utility — нет вклада в weapon DPS. Per-stack armor regen ramp (community описывает hit-driven регенерацию) не моделируется.',
    },
  },
  {
    // raw gearsets.json (gear_set_g System Corruption): ArmorOnKillModBonus 15%,
    // 40% bonus ref (0), 40% bonus ref (1). Y8S1 status build.
    // Community canon (sparse, June 2026): kills apply system-corruption debuff to
    // nearby enemies → bonus Status Effects damage. Utility set — no direct WD.
    setId: 'system_corruption',
    name: { en: 'System Corruption', ru: 'Коррупция в системе' },
    trigger: 'kill',
    triggerNote: {
      en: 'Y8S1 status build. Kills apply corruption debuff. raw: +15% ArmorOnKill + 40% + 40% refs.',
      ru: 'Y8S1 status билд. Убийства поджигают corruption debuff. raw: +15% ArmorOnKill + 40% + 40% refs.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ needs in-game verification — raw incomplete. Confirmed baseline: +15% Armor on Kill + two 40% bonus refs (likely Status Effects damage and/or corruption-debuff WD amp). Y8S1 status set — no direct WD contribution. Peak % and bucket (additive vs amp) need verification.',
      ru: '⚠ требует проверки in-game — raw неполный. Базы: +15% Armor on Kill + два 40% bonus refs (вероятно Status Effects урон и/или corruption-debuff WD amp). Y8S1 status сет — прямого WD нет. Пик % и bucket (additive vs amp) требуют проверки.',
    },
  },
  {
    // raw gearsets.json (gear_set_m Rigger): SkillHasteModBonus 15%,
    // allskills_duration_mod_Bonus 15%. Pure skill-build utility.
    // No direct weapon DPS contribution.
    setId: 'rigger',
    name: { en: 'Rigger', ru: 'Такелажник' },
    trigger: 'proc',
    triggerNote: {
      en: 'Skill build. raw: +15% Skill Haste, +15% Skill Duration. Pure skill utility — no WD.',
      ru: 'Skill билд. raw: +15% Skill Haste, +15% Skill Duration. Чистая skill utility — WD нет.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ needs in-game verification — raw incomplete. Confirmed baseline: +15% Skill Haste + +15% Skill Duration. Skill-tier set — no weapon DPS contribution. Community-reported skill-ammo regen / signature-ammo mechanic from chest/bp talents not modeled.',
      ru: '⚠ требует проверки in-game — raw неполный. Базы: +15% Skill Haste + +15% Skill Duration. Skill сет — нет вклада в weapon DPS. Сообщество описывает skill-ammo regen / signature-ammo от жилета/рюкзака — не моделируется.',
    },
  },
  {
    // raw gearsets.json (gear_set_s Aegis): HealthModBonus 70%, ArmorModBonus 15%.
    // Defensive utility — survivability via massive health pool + armor.
    // Community: chest/bp likely armor-kit / health-on-armor-kit mechanics.
    // No direct weapon DPS contribution.
    setId: 'aegis',
    name: { en: 'Aegis', ru: 'Эгида' },
    trigger: 'proc',
    triggerNote: {
      en: 'Defensive set. raw: +70% Health, +15% Armor. Survivability utility — no direct WD.',
      ru: 'Защитный сет. raw: +70% Health, +15% Armor. Survivability utility — прямого WD нет.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: '⚠ needs in-game verification — raw incomplete. Confirmed baseline: +70% Health + +15% Armor. Defensive set — no weapon DPS contribution. Chest/backpack armor-kit on-heal mechanics (community) not modeled.',
      ru: '⚠ требует проверки in-game — raw неполный. Базы: +70% Health + +15% Armor. Защитный сет — нет вклада в weapon DPS. Армор-кит on-heal механики жилета/рюкзака (сообщество) не моделируются.',
    },
  },
  {
    // raw gearsets.json (gear_set_r Cavalier): AllStatusResistancePercentageFlatBonus
    // 30% (≈ hazard protection 30), allskills_healing_mod_Bonus 40% (≈ skill repair 40).
    // Defensive / skill-support utility. No direct weapon DPS contribution.
    setId: 'cavalier',
    name: { en: 'Cavalier', ru: 'Кавалер' },
    trigger: 'proc',
    triggerNote: {
      en: 'Defensive/skill support. raw: +30% Hazard Protection, +40% Skill Repair. Utility — no direct WD.',
      ru: 'Защита/skill саппорт. raw: +30% Hazard Protection, +40% Skill Repair. Utility — прямого WD нет.',
    },
    maxBase: 1,
    perStack: [{ stat: 'wd', value: 0 }],
    note: {
      en: 'Confirmed from raw: +30% Hazard Protection (AllStatusResistance flat) + +40% Skill Repair (allskills_healing_mod). Utility set — no weapon DPS contribution. Chest/backpack mechanics (community: armor-on-skill-use, ally support) not modeled — needs in-game verification for peak utility scaling.',
      ru: 'Подтверждено из raw: +30% Hazard Protection (AllStatusResistance flat) + +40% Skill Repair (allskills_healing_mod). Utility сет — нет вклада в weapon DPS. Жилет/рюкзак механики (сообщество: armor-on-skill-use, ally support) не моделируются — требуется проверка in-game пика utility scaling.',
    },
  },
];

export function findSet4pc(setId: string): SetStacks | undefined {
  return SET_4PC_STACKS.find((s) => s.setId === setId);
}
